import React, {useEffect, useMemo} from 'react';
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom';
import {TNOidcWrapper} from './tnoidc-wrapper';
import {A11yContextProvider} from '@touchnet/react-a11y';
import {TnLoaderOverlay} from '@touchnet/react-loader';
import {TnToastContainer} from '@touchnet/react-toast';
import {UserMgmtContextProvider} from '@touchnet/user-management';

import type {TnUserMgmtEnv} from '@touchnet/user-management';

import App from '~/app';
import WhiteoutWrapper from '~/whiteout';
import useUrpsConfigRequest from './hooks/use-urps-config-request';

import TnAdminRedirect from "~/components/pages/tn-admin-redirect";
import ConnectedIntlProvider from '~/intl-provider';
import routes, {getRoute} from "~/route-map";
import LogoutPage from '~/components/pages/logout-page';
import {useSelector} from "react-redux";
import Selectors from "~/state/selectors.ts";
import ApiKeyPage from "~/components/pages/api-key-page.tsx";

const CHANNEL = "cmm-embedded";

// Handles logout requests from the CMM Admin through postMessage
export function EmbeddedLogoutListener() {
    const navigate = useNavigate();

    const cnum: string = useSelector(Selectors.getCustomerNumber);
    const ctx: string = useSelector(Selectors.getAppContext);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;

            if (event.source !== window.parent) return;

            const data = event.data;

            if (data?.type !== "LOGOUT_REQUEST") return;
            if (data?.channel !== CHANNEL) return;

            navigate(getRoute(routes.app.logout, {cnum, ctx}), {replace: true});
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [navigate, cnum, ctx]);
    return null;
}

export const AuthWrapper = (): JSX.Element => {
    const cnum = useSelector(Selectors.getCustomerNumber);
    const ctx = useSelector(Selectors.getAppContext);

    const appEnvState = useSelector(Selectors.getAppEnv);

    const appEnv = appEnvState.env;

    const { urpsConfig, loading, error } = useUrpsConfigRequest();

    const userMgmtEnv: TnUserMgmtEnv = useMemo<TnUserMgmtEnv>(
        () => ({
            cnum: cnum,
            oidc: {
                clientId: appEnv.oidc.clientId,
                redirectUri: appEnv.oidc.urls.postLogin,
                serviceUri: appEnv.oidc.authBaseUrl,
            },
            urps: {
                serviceUri: appEnv.urps.serviceUri,
            },
            userService: {
                realmId: appEnv.usersvc.realmId,
                serviceUri: appEnv.usersvc.baseUri,
            },
        }),
        [cnum, appEnv]
    );

    if (loading) {
        return (<WhiteoutWrapper>Loading</WhiteoutWrapper>);
    }

    if (error) {
        return (
            <WhiteoutWrapper>
                Failed to load configuration
            </WhiteoutWrapper>
        );
    }

    return (
        <Routes>
            <Route path={routes.tn}  element={<TnAdminRedirect />} />
            <Route
                path="*"
                element={(
                    <TNOidcWrapper>
                        <ConnectedIntlProvider>
                            <EmbeddedLogoutListener />
                            <Routes>
                                <Route
                                    path="/"
                                    element={<Navigate to={getRoute(routes.app.base, {cnum, ctx})} replace />}
                                />
                                <Route path={routes.app.logout} element={<LogoutPage />} />
                                <Route
                                    path={`${routes.app.base}/api-key`}
                                    element={(
                                        <>
                                            <A11yContextProvider>
                                                <UserMgmtContextProvider env={userMgmtEnv} urpsConfig={urpsConfig}>
                                                    <ApiKeyPage />
                                                </UserMgmtContextProvider>
                                            </A11yContextProvider>
                                            <TnLoaderOverlay/>
                                            <TnToastContainer {...{autoClose: 8000}} />
                                        </>
                                    )}
                                />
                                <Route
                                    path={`${routes.app.base}/api-key/*`}
                                    element={<Navigate to={`${getRoute(routes.app.base, {cnum, ctx})}/api-key`} replace />}
                                />
                                <Route 
                                    path={`${routes.app.base}/*`}
                                    element={(
                                        <>
                                            <A11yContextProvider>
                                                <UserMgmtContextProvider env={userMgmtEnv} urpsConfig={urpsConfig}>
                                                    <App/>
                                                </UserMgmtContextProvider>
                                            </A11yContextProvider>
                                            <TnLoaderOverlay/>
                                            <TnToastContainer {...{autoClose: 8000}} />
                                        </>
                                    )}
                                />
                            </Routes>
                        </ConnectedIntlProvider>
                    </TNOidcWrapper>
                )}
            />
        </Routes>
    );
};

export default AuthWrapper;
